import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { DesignService } from "./design.service";

@Injectable({ providedIn: "root" })
export class IconService {
  // Emits current base folder for icons (e.g. '/assets/icons' or '/assets/design-modern/icons')
  private base$ = new BehaviorSubject<string>(this.computeBase());
  // Cache fetched SVG text by absolute URL to avoid duplicate network requests
  private svgCache = new Map<string, string>();

  constructor(private design: DesignService) {
    // update base when design changes
    this.design.design$.subscribe(() => this.base$.next(this.computeBase()));
  }

  getIconUrl(name: string): string {
    const base = this.computeBase();
    return `${base}/${name}.svg`;
  }

  /**
   * Build URL from path parts, e.g. ['folder','icon'] -> /base/folder/icon.svg
   */
  getIconUrlParts(parts: string[]): string {
    const base = this.computeBase();
    const safeParts = (parts || [])
      .map((p) => String(p).trim())
      .filter(Boolean);
    if (safeParts.length === 0) return `${base}.svg`;
    return `${base}/${safeParts.join("/")}.svg`;
  }

  /**
   * Get SVG text for the given URL with in-memory caching.
   */
  async getSvg(url: string): Promise<string> {
    const cached = this.svgCache.get(url);
    if (cached) return cached;

    try {
      // Добавляем таймаут для fetch запроса (3 секунды)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);

      if (!res.ok) throw new Error(`Failed to load icon: ${url}`);
      const text = await res.text();
      this.svgCache.set(url, text);
      return text;
    } catch (error) {
      // Логируем ошибку, но не бросаем исключение, чтобы не блокировать приложение
      console.warn(`Icon load failed (non-critical): ${url}`, error);
      return ""; // Возвращаем пустую строку вместо ошибки
    }
  }

  /** Synchronous cache read; returns SVG text if already cached */
  getCachedSvg(url: string): string | undefined {
    return this.svgCache.get(url);
  }

  /**
   * Preload multiple icons by path parts (e.g. ['Charts','chart-pie']).
   */
  preloadByParts(list: string[][]): void {
    try {
      for (const parts of list || []) {
        const url = this.getIconUrlParts(parts);
        if (!this.svgCache.has(url)) {
          // fire and forget
          this.getSvg(url).catch(() => {});
        }
      }
    } catch {}
  }

  /**
   * Preload icons and return a promise that resolves when all are fetched.
   * Useful for APP_INITIALIZER to block bootstrap until ready.
   */
  preloadByPartsAsync(list: string[][]): Promise<void> {
    const promises: Promise<any>[] = [];
    try {
      for (const parts of list || []) {
        const url = this.getIconUrlParts(parts);
        if (!this.svgCache.has(url)) {
          promises.push(this.getSvg(url).catch(() => {}));
        }
      }
    } catch {}
    return Promise.all(promises).then(() => undefined);
  }

  // Expose observable if callers want to react to base changes
  get basePath$() {
    return this.base$.asObservable();
  }

  private computeBase(): string {
    try {
      const d = this.design.getDesign();
      if (d === "modern") return "/assets/design-modern/icons";
    } catch (e) {
      // ignore
    }
    return "/assets/icons";
  }
}
